import apiClient from "./client";
import type { ApiErrorResponse } from "./events";

export type GiftCategory = {
  id: string | null;
  name: string;
};

export type GiftItem = {
  id: string;
  name: string;
  description?: string | null;
  amount: number;
  location?: string | null;
  link?: string | null;
  cover?: string | null;
  order: number;
  enabled: boolean;
  cash: boolean;
  category?: GiftCategory | null;
  totalContributions: number;
  remainingAmount: number;
  currency: string;
};

export type GiftIndexResponse = {
  gifts: GiftItem[];
  categories: GiftCategory[];
};

export type GiftItemPayload = {
  name: string;
  description?: string | null;
  amount?: number | null;
  location?: string | null;
  link?: string | null;
  cover?: string | null;
  order?: number | null;
  enabled?: boolean;
  // UUID of category (public-safe). Backend still supports numeric fallback for legacy clients.
  categoryId?: string | null;
  categoryName?: string | null;
};

const unwrap = async <T>(promise: Promise<{ data: T | ApiErrorResponse }>): Promise<T> => {
  const response = await promise;
  const data = response.data;
  if (data !== null && typeof data === "object" && "error" in data) {
    throw new Error((data as ApiErrorResponse).error?.message || "Gift API error");
  }
  return data as T;
};

export async function getEventGifts(eventId: string): Promise<GiftIndexResponse> {
  return unwrap(
    apiClient.get<GiftIndexResponse | ApiErrorResponse>(`/events/${eventId}/gifts`),
  );
}

export async function createGift(eventId: string, payload: GiftItemPayload): Promise<GiftItem> {
  return unwrap(
    apiClient.post<GiftItem | ApiErrorResponse>(`/events/${eventId}/gifts`, payload),
  );
}

export async function updateGift(
  eventId: string,
  giftId: string,
  payload: Partial<GiftItemPayload>,
): Promise<GiftItem> {
  return unwrap(
    apiClient.put<GiftItem | ApiErrorResponse>(`/events/${eventId}/gifts/${giftId}`, payload),
  );
}

export async function deleteGift(eventId: string, giftId: string): Promise<void> {
  await unwrap(
    apiClient.delete<null | ApiErrorResponse>(`/events/${eventId}/gifts/${giftId}`),
  );
}

